import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import { formatCurrency } from '../../utils/formatters';
import { FilterState } from '../../types/filterTypes';

interface ReportListItem {
  id: string;
  title: string;
  period: {
    month: number;
    year: number;
  };
  createdAt: Date;
  reportType: 'monthly' | 'quarterly' | 'annual';
  kpiSummary?: {
    totalSales: number;
    adSpend: number;
    netProfit: number;
  };
  isInteractive?: boolean;
}

const ReportsPage: React.FC = () => {
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useMockData, setUseMockData] = useState(false);
  const navigate = useNavigate();

  // Get month name from month number
  const getMonthName = (month: number): string => {
    return new Date(2000, month - 1).toLocaleString('default', { month: 'long' });
  };

  // Format report period based on report type
  const formatPeriod = (report: ReportListItem): string => {
    if (report.reportType === 'monthly') {
      return `${getMonthName(report.period.month)} ${report.period.year}`;
    } else if (report.reportType === 'quarterly') {
      const quarter = Math.ceil(report.period.month / 3);
      return `Q${quarter} ${report.period.year}`;
    } else {
      return `${report.period.year}`;
    }
  };

  // Fetch reports from API
  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      setError(null);

      try {
        // Always start with mock data for demo purposes
        setUseMockData(true);

        try {
          const fetchedReports = await apiService.getReports();

          // Only proceed if we have actual data from the API
          if (fetchedReports && Array.isArray(fetchedReports.data) && fetchedReports.data.length > 0) {
            console.log('Using actual data from API');

            // Transform data to match our interface
            const reportList: ReportListItem[] = fetchedReports.data.map((report: any) => ({
              id: report.id,
              title: report.title,
              period: report.period || { month: new Date().getMonth() + 1, year: new Date().getFullYear() },
              createdAt: new Date(report.created_at || report.createdAt),
              reportType: report.period || 'monthly',
              isInteractive: true, // All reports are interactive
              kpiSummary: {
                totalSales: Math.round(Math.random() * 200000 + 100000),
                adSpend: Math.round(Math.random() * 20000 + 5000),
                netProfit: Math.round(Math.random() * 80000 + 20000)
              }
            }));

            // Sort by creation date (newest first)
            reportList.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

            setReports(reportList);
            setUseMockData(false);
            setError(null);
            return;
          }
        } catch (apiErr) {
          console.error('Error fetching reports from API:', apiErr);
          // Continue to use mock data
        }

        console.log('Using mock data for demo');
        setError('Connected to demo environment. Using sample data.');

        // Fallback to mock data
        const mockReports: ReportListItem[] = [
          {
            id: 'sample-report-1',
            title: 'March 2023 Marketing Performance',
            period: { month: 3, year: 2023 },
            createdAt: new Date(2023, 3, 5),
            reportType: 'monthly',
            kpiSummary: {
              totalSales: 167850,
              adSpend: 12500,
              netProfit: 56470
            },
            isInteractive: true
          },
          {
            id: 'sample-report-2',
            title: 'Q1 2023 Performance Summary',
            period: { month: 3, year: 2023 },
            createdAt: new Date(2023, 3, 10),
            reportType: 'quarterly',
            kpiSummary: {
              totalSales: 452300,
              adSpend: 34750,
              netProfit: 143400
            },
            isInteractive: true
          }
        ];
        
        setReports(mockReports);
        setUseMockData(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchReports();
  }, []);

  // Handle report deletion
  const handleDeleteReport = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!confirm('Are you sure you want to delete this report?')) {
      return;
    }
    
    try {
      if (!useMockData) {
        await apiService.deleteReport(id);
      }
      
      // Update state to remove the deleted report
      setReports(reports.filter(report => report.id !== id));
    } catch (err) {
      console.error('Error deleting report:', err);
      setError('Failed to delete report');
    }
  };

  // Handle report click - navigate to report detail view with interactive flag
  const handleReportClick = (report: ReportListItem) => {
    // All reports are now treated as interactive by default
    navigate(`/reports/${report.id}`, {
      state: {
        isInteractive: true,
        reportType: report.reportType,
        reportPeriod: report.period
      }
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Marketing Reports</h1>
        <Link 
          to="/upload" 
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          Generate New Report
        </Link>
      </div>
      
      {error && (
        <div className="bg-yellow-50 border border-yellow-400 text-yellow-700 p-4 rounded mb-6">
          <p>{error}</p>
        </div>
      )}
      
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            ></path>
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">No reports found</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by generating your first marketing report.</p>
          <div className="mt-6">
            <Link
              to="/upload"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              Upload Marketing Data
            </Link>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Report Title</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Period</th>
                <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Type</th>
                <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Total Sales</th>
                <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Ad Spend</th>
                <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">Net Profit</th>
                <th scope="col" className="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Features</th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {reports.map((report) => (
                <tr
                  key={report.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleReportClick(report)}
                >
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-blue-600 sm:pl-6">{report.title}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatPeriod(report)}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 capitalize">{report.reportType}</td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                    {report.kpiSummary ? formatCurrency(report.kpiSummary.totalSales) : 'N/A'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                    {report.kpiSummary ? formatCurrency(report.kpiSummary.adSpend) : 'N/A'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                    {report.kpiSummary ? formatCurrency(report.kpiSummary.netProfit) : 'N/A'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
                    {report.isInteractive && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Interactive
                      </span>
                    )}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <button
                      className="text-red-600 hover:text-red-900"
                      onClick={(e) => handleDeleteReport(report.id, e)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      <div className="mt-8">
        <div className="bg-gray-50 p-4 rounded-lg">
          <h2 className="text-lg font-semibold mb-2">About Marketing Reports</h2>
          <p className="text-gray-600 mb-2">
            Marketing reports provide insights into the performance of your marketing campaigns,
            including sales data, ad spend, and ROI metrics.
          </p>
          <p className="text-gray-600 mb-4">
            To generate a new report, upload your sales and ad spend data through the &quot;Generate New Report&quot; button above.
          </p>
          
          <div className="mt-4 border-t pt-4">
            <h3 className="text-lg font-semibold mb-4 text-center">Available Static Reports</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Standard Reports Column */}
              <div>
                <h4 className="text-md font-semibold text-gray-700 mb-3">Standard Reports</h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-500 uppercase tracking-wider">Monthly</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <Link
                        to="/january-report"
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        January 2025
                      </Link>
                      <Link
                        to="/february-report"
                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                      >
                        February 2025
                      </Link>
                      <Link
                        to="/dashboard-march-report"
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        March 2025
                      </Link>
                      <Link
                        to="/april-report"
                        className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700"
                      >
                        April 2025
                      </Link>
                      <Link
                        to="/may-report"
                        className="px-3 py-1.5 text-sm bg-rose-600 text-white rounded hover:bg-rose-700"
                      >
                        May 2025
                      </Link>
                      <Link
                        to="/june-report"
                        className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded hover:bg-cyan-700"
                      >
                        June 2025
                      </Link>
                      <Link
                        to="/july-report"
                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        July 2025
                      </Link>
                      <Link
                        to="/august-report"
                        className="px-3 py-1.5 text-sm bg-slate-600 text-white rounded hover:bg-slate-700"
                      >
                        August 2025
                      </Link>
                      <Link
                        to="/september-report"
                        className="px-3 py-1.5 text-sm bg-violet-600 text-white rounded hover:bg-violet-700"
                      >
                        September 2025
                      </Link>
                      <Link
                        to="/october-report"
                        className="px-3 py-1.5 text-sm bg-fuchsia-600 text-white rounded hover:bg-fuchsia-700"
                      >
                        October 2025
                      </Link>
                      <Link
                        to="/november-report"
                        className="px-3 py-1.5 text-sm bg-pink-600 text-white rounded hover:bg-pink-700"
                      >
                        November 2025
                      </Link>
                      <Link
                        to="/december-report"
                        className="px-3 py-1.5 text-sm bg-sky-600 text-white rounded hover:bg-sky-700"
                      >
                        December 2025
                      </Link>
                    </div>
                  </div>

                  <div className="mt-3">
                    <span className="text-sm text-gray-500 uppercase tracking-wider">Quarterly</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <Link
                        to="/q1-report"
                        className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
                      >
                        Q1 2025
                      </Link>
                      <Link
                        to="/q2-report"
                        className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                      >
                        Q2 2025
                      </Link>
                      <Link
                        to="/q3-report"
                        className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded hover:bg-teal-700"
                      >
                        Q3 2025
                      </Link>
                      <Link
                        to="/q4-report"
                        className="px-3 py-1.5 text-sm bg-neutral-600 text-white rounded hover:bg-neutral-700"
                      >
                        Q4 2025
                      </Link>
                    </div>
                  </div>

                  <div className="mt-3">
                    <span className="text-sm text-gray-500 uppercase tracking-wider">Year-to-Date</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <Link
                        to="/ytd-report"
                        className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700"
                      >
                        YTD 2025
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Cody Cline Reports Column */}
              <div>
                <h4 className="text-md font-semibold text-gray-700 mb-3">Cody Cline Reports</h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-gray-500 uppercase tracking-wider">Monthly</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <Link
                        to="/cc-january-report"
                        className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        CC-January
                      </Link>
                      <Link
                        to="/cc-february-report"
                        className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
                      >
                        CC-February
                      </Link>
                      <Link
                        to="/cc-march-report"
                        className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        CC-March
                      </Link>
                      <Link
                        to="/cc-april-report"
                        className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700"
                      >
                        CC-April
                      </Link>
                      <Link
                        to="/cc-may-report"
                        className="px-3 py-1.5 text-sm bg-rose-600 text-white rounded hover:bg-rose-700"
                      >
                        CC-May
                      </Link>
                      <Link
                        to="/cc-june-report"
                        className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded hover:bg-cyan-700"
                      >
                        CC-June
                      </Link>
                      <Link
                        to="/cc-july-report"
                        className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        CC-July
                      </Link>
                      <Link
                        to="/cc-august-report"
                        className="px-3 py-1.5 text-sm bg-teal-600 text-white rounded hover:bg-teal-700"
                      >
                        CC-August
                      </Link>
                      <Link
                        to="/cc-september-report"
                        className="px-3 py-1.5 text-sm bg-lime-600 text-white rounded hover:bg-lime-700"
                      >
                        CC-September
                      </Link>
                      <Link
                        to="/cc-october-report"
                        className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded hover:bg-cyan-700"
                      >
                        CC-October
                      </Link>
                      <Link
                        to="/cc-november-report"
                        className="px-3 py-1.5 text-sm bg-lime-600 text-white rounded hover:bg-lime-700"
                      >
                        CC-November
                      </Link>
                      <Link
                        to="/cc-december-report"
                        className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                      >
                        CC-December
                      </Link>
                    </div>
                  </div>

                  <div className="mt-3">
                    <span className="text-sm text-gray-500 uppercase tracking-wider">Quarterly</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <Link
                        to="/cc-q1-report"
                        className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
                      >
                        CC-Q1 2025
                      </Link>
                      <Link
                        to="/cc-q2-report"
                        className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                      >
                        CC-Q2 2025
                      </Link>
                      <Link
                        to="/cc-q3-report"
                        className="px-3 py-1.5 text-sm bg-pink-600 text-white rounded hover:bg-pink-700"
                      >
                        CC-Q3 2025
                      </Link>
                      <Link
                        to="/cc-q4-report"
                        className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded hover:bg-amber-700"
                      >
                        CC-Q4 2025
                      </Link>
                    </div>
                  </div>

                  <div className="mt-3">
                    <span className="text-sm text-gray-500 uppercase tracking-wider">Year-to-Date</span>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <Link
                        to="/cc-ytd-report"
                        className="px-3 py-1.5 text-sm bg-stone-600 text-white rounded hover:bg-stone-700"
                      >
                        CC-YTD 2025
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;